import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { StatusTone } from '@/components/ui/status-badge';
import type {
  ConvertLeadInput,
  ConvertLeadResult,
  CreateLeadInput,
  LeadAssignee,
  LeadAssignmentInput,
  LeadDetail,
  LeadDuplicateView,
  LeadListResponse,
  LeadNotesInput,
  LeadSourceValue,
  LeadStatusInput,
  LeadStatusValue,
} from '@shared/schemas/lead';
import type { RecordSort } from '@shared/schemas/pagination';

/* The queries behind /admin/leads. ROADMAP.md §9.8 and §9.9. */

const ROOT = ['admin', 'leads'] as const;

/**
 * The badge tone per state — the one mapping, so no screen invents its own.
 *
 * `lost` is `cancelled`-toned rather than red. An enquiry that went nowhere is a
 * normal outcome, not an alarm, and it can come back — the transitions let it.
 */
export const LEAD_TONES: Record<LeadStatusValue, StatusTone> = {
  new: 'pending',
  contacted: 'scheduled',
  qualified: 'in-progress',
  converted: 'completed',
  lost: 'cancelled',
};

/**
 * Converting creates a **client**, so the clients cache goes stale with it —
 * otherwise the customer just created is missing from the client list until a
 * reload, which reads as the conversion not having worked.
 */
function invalidate(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ROOT }),
    queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] }),
  ]).then(() => undefined);
}

export interface LeadListParams {
  sort: RecordSort;
  page: number;
  status: LeadStatusValue | 'all';
  search: string;
  source: LeadSourceValue | '';
  assignedTo: string;
}

export function useLeads(params: LeadListParams) {
  return useQuery({
    queryKey: [...ROOT, 'list', params],
    queryFn: () =>
      api.get<LeadListResponse>(
        `/api/admin/leads${queryString({
          page: params.page,
          sort: params.sort,
          status: params.status,
          search: params.search === '' ? undefined : params.search,
          source: params.source === '' ? undefined : params.source,
          assignedTo: params.assignedTo === '' ? undefined : params.assignedTo,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: [...ROOT, 'detail', id],
    queryFn: () => api.get<LeadDetail>(`/api/admin/leads/${id ?? ''}`),
    enabled: id !== undefined && id !== '',
  });
}

export function useLeadAssignees() {
  return useQuery({
    queryKey: [...ROOT, 'assignees'],
    queryFn: () => api.get<{ assignees: LeadAssignee[] }>('/api/admin/leads/assignees'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Record an enquiry the office took down itself — a call, a referral, a walk-in.
 *
 * Invalidates the list the same way every other lead mutation does, so the new
 * row appears in whatever filter is open rather than only after a refresh.
 */
export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeadInput) => api.post<LeadDetail>('/api/admin/leads', input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useSetLeadStatus(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LeadStatusInput) =>
      api.post<LeadDetail>(`/api/admin/leads/${leadId}/status`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useAssignLead(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LeadAssignmentInput) =>
      api.post<LeadDetail>(`/api/admin/leads/${leadId}/assign`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useSetLeadNotes(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LeadNotesInput) =>
      api.patch<LeadDetail>(`/api/admin/leads/${leadId}/notes`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

/**
 * "Not the same somebody" — the only thing that can be done to a possible
 * duplicate.
 *
 * **There is no merge mutation here and there should not be one.** Owner
 * instruction, 2026-08-07: *"never auto-merged"*, and nobody has asked for a
 * manual merge. The office's honest moves are already on the screen.
 */
export function useDismissLeadDuplicate(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (duplicateId: string) =>
      api.post<{ duplicates: LeadDuplicateView[] }>(
        `/api/admin/leads/${leadId}/duplicates/${duplicateId}/dismiss`,
        {},
      ),
    onSuccess: () => invalidate(queryClient),
  });
}

/** What convert returns, plus the two things only the calling screen needs. */
export interface ConvertLeadResponse extends ConvertLeadResult {
  temporaryPassword: string | null;
  /** Why no portal login was made, when none was. */
  portalLoginNote: string | null;
}

export function useConvertLead(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConvertLeadInput) =>
      api.post<ConvertLeadResponse>(`/api/admin/leads/${leadId}/convert`, input),
    onSuccess: () => invalidate(queryClient),
  });
}
